import { Edit } from "@mui/icons-material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, Card, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, Paper, Tab, Tabs, Typography } from "@mui/material";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import type { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import { alpha, useTheme } from "@mui/material/styles";
import { DateTime } from "luxon";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Area, CartesianGrid, ComposedChart, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import CopilotButton from "../../components/CopilotButton/CopilotButton";
import FloatingNursesRecommendation from "../../components/FloatingNursesRecommendation/FloatingNursesRecommendation";
import { SegmentedProgressBar } from "../../components/segmentedProgressBar/SegmentedProgressBar";
import { TopRightPane } from "../../components/staffing/Staffing";
import { CreateShiftContent } from "../../components/staffingSearch/CreateShiftContent";
import { ThemedSvg } from "../../components/UIToolkit/ThemedSvg";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useCommandCenter } from "../../hooks/useCommandCenter";
import i18n from "../../i18n/i18n";
import { CHART_SERIES, HOSPITAL_DEMO, INK, INK_BLACK, NEUTRAL, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { deepCloneObject } from "../../utils/dateUtils";
import type {
	Assignment,
	CensusDataPoint,
	CommandCenterNurse,
	DepartmentSummary,
	FloatingNursesRecData,
	HourlyCensus,
	NurseFullRole,
	PPNDataPoint,
} from "../../utils/models";
import axiosWrapper from "../../utils/networkWrapper";
import { inverseRolesMap, safeSub, safeSum } from "../../utils/utilFunctions";
import Styles from "./CommandCenter.module.scss";
import HalfCircle from "./TripleGauge";

enum TimeFrame {
	REAL_TIME = "real_time",
	ONE_WEEK = "1_week",
	TWO_WEEKS = "2_weeks",
}

// Functional / Categorical: chart series colors must stay stable across user themes
// so the legend remains readable. Distinct hues encode different data series — the
// stable hues live in `CHART_SERIES` (tokens). The label/band tints intentionally
// track the user's primary via the published CSS variable.
const CHART_COLORS = {
	CENSUS: CHART_SERIES.CENSUS,
	CENSUS_LABEL: "var(--inhouse-primary-alpha-08)",
	PPN: CHART_SERIES.PPN,
	REFERENCE_BAND: "var(--inhouse-primary-alpha-08)",
	OUT_OF_BOUNDS: CHART_SERIES.OUT_OF_BOUNDS,
} as const;

// Hospital demo palette: blue buttons, dark PPN line, turquoise target band.
const DEMO_BLUE = "#448AFF";
const DEMO_BLUE_DARK = "#2C6FE0";
const DEMO_CHART = {
	LINE: HOSPITAL_DEMO.CHART_LINE, // dark actual-PPN line
	TARGET_BAND: HOSPITAL_DEMO.CHART_BAND, // turquoise target range
	CENSUS_LINE: HOSPITAL_DEMO.CHART_LINE, // census line — matches the PPN tab's dark Actual line
} as const;

function get_dept_patient_score(dept: DepartmentSummary) {
	const fixedScore = dept.wls.toFixed(1);
	return Number.parseFloat(fixedScore) % 1 === 0 ? Number.parseInt(fixedScore).toString() : fixedScore;
}

function getEverySecondHour(censusMap: Array<HourlyCensus> | undefined) {
	if (!censusMap) return [];
	return censusMap.map((item: HourlyCensus) => item.hour).filter((_, index) => index % 2 === 0);
}

const formatHourInHebrew = (hour: number) => {
	return `${hour}:00`;
};

// First, let's create a proper type for the range data
interface RangeDataPoint {
	value: [number, number];
	dataKey: string;
}

interface ShowBedOccupancyProps {
	total_beds: number;
	census: number;
}

const ShowBedOccupancy = memo(({ total_beds, census }: ShowBedOccupancyProps) => {
	const { t, i18n } = useTranslation();
	return (
		<Grid container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center" }}>
			<SegmentedProgressBar value={census} total={total_beds} width={150} height={15} />

			<Typography
				variant="body1"
				component="div"
				sx={{
					fontWeight: "bold",
					lineHeight: "12px",
					marginLeft: "10px",
					marginRight: "10px",
					marginTop: "15px",
				}}
			>
				{i18n.dir() === "rtl" ? (
					<>
						{" "}
						{total_beds} / {census}
					</>
				) : (
					<>
						{" "}
						{census} / {total_beds}
					</>
				)}
				<br />
				<span style={{ fontSize: "12px", fontWeight: "normal" }}>{t("Bed Occupancy")}</span>
			</Typography>
		</Grid>
	);
});

interface PPNDisplayProps {
	actualPPN: number;
	targetPPN: number;
	smallScale?: boolean;
	// Demo stacks "target optimal" under the PPN value with no divider (instead of PPN | optimal).
	isDemo?: boolean;
}

const PPNDisplay = memo(({ actualPPN, targetPPN, smallScale = false, isDemo = false }: PPNDisplayProps) => {
	return (
		<Grid container sx={{ flexDirection: smallScale ? "row" : "column", justifyContent: "space-between", alignItems: "center" }}>
			{smallScale ? (
				// Small scale layout - horizontal
				<Grid
					container
					direction="row"
					sx={{
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
					}}
					wrap="nowrap"
				>
					<Grid size={5} sx={{ marginBottom: "-40px" }}>
						<HalfCircle value={((actualPPN - targetPPN) / targetPPN) * 200} size={70} />
					</Grid>
					<Grid
						container
						size={7}
						direction="row"
						sx={{
							alignItems: "center",
							display: "flex",
							flexDirection: "row",
						}}
						wrap="nowrap"
					>
						{isDemo ? (
							// Demo: "target optimal" stacked under the PPN value, no divider.
							<Grid sx={{ display: "flex", flexDirection: "column", alignItems: "center", ml: 3 }}>
								<span style={{ fontWeight: 500, fontSize: "14px" }}>PPN {actualPPN}</span>
								<span style={{ fontSize: "10px", color: NEUTRAL[600], lineHeight: "10px" }}>{targetPPN} optimal</span>
							</Grid>
						) : (
							<>
								<h2
									style={{
										fontWeight: 500,
										margin: 0,
										marginRight: 5,
										fontSize: "14px",
									}}
								>
									PPN
								</h2>
								<span style={{ fontWeight: 500, margin: 0, fontSize: "14px" }}>{actualPPN}</span>
								<span
									style={{
										fontSize: "16px",
										width: "15px",
										textAlign: "center",
										color: NEUTRAL[600],
									}}
								>
									|
								</span>
								<Grid
									sx={{
										fontSize: "10px",
										marginLeft: 0.5,
										color: NEUTRAL[600],
										lineHeight: "10px",
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
									}}
								>
									<span>{targetPPN}</span>
									<span>optimal</span>
								</Grid>
							</>
						)}
					</Grid>
				</Grid>
			) : (
				// Original layout - vertical
				<>
					{isDemo ? (
						// Demo: "target optimal" stacked under the PPN value, no divider.
						<Grid container sx={{ flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
							<span style={{ fontWeight: 500, fontSize: "16px" }}>PPN {actualPPN}</span>
							<span style={{ fontSize: "11px", fontWeight: 300, color: NEUTRAL[600], lineHeight: "12px" }}>{targetPPN} optimal</span>
						</Grid>
					) : (
						<Grid container direction="row" sx={{ justifyContent: "center", alignItems: "center" }} wrap="nowrap">
							<h2
								style={{
									fontWeight: 500,
									margin: 0,
									marginRight: 5,
									fontSize: "16px",
								}}
							>
								PPN
							</h2>
							<span style={{ fontWeight: 500, margin: 0, fontSize: "16px" }}>{actualPPN}</span>
							<span
								style={{
									margin: 0,
									fontSize: "16px",
									width: "20px",
									textAlign: "center",
									color: NEUTRAL[600],
								}}
							>
								|
							</span>
							<Grid
								container
								sx={{
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									width: "25px",
									fontSize: "11px",
									fontWeight: 300,
									color: NEUTRAL[600],
									lineHeight: "12px",
								}}
							>
								<span>{targetPPN}</span>
								<span>optimal</span>
							</Grid>
						</Grid>
					)}
					<HalfCircle value={((actualPPN - targetPPN) / targetPPN) * 200} size={100} />
					<h6
						style={{
							margin: 0,
							fontSize: "8px",
							fontWeight: 400,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					/>
				</>
			)}
		</Grid>
	);
});

// First, let's update the renderValueLabel function definition to handle null cases
const renderValueLabel = (activePoint: number | null, props: any, color: string) => {
	const { value, x, y, index } = props;
	if (activePoint === null || index !== activePoint) return null;
	if (value === undefined) return null;
	return (
		<g>
			<circle cx={x} cy={y - 15} r={12} fill={color} />
			<text x={x} y={y - 12} textAnchor="middle" fill={SURFACE} fontSize="12" fontWeight="bold">
				{value.toFixed(2)}
			</text>
		</g>
	);
};

const formatXAxisLabel = (selectedTimeFrame: TimeFrame, date: string, timezone: string) => {
	if (!date) return "";

	const currentDate = new Date(date);

	if (selectedTimeFrame === TimeFrame.REAL_TIME) {
		// For real-time, only show hours
		const timeFormatter = new Intl.DateTimeFormat(i18n.language === "he" ? "he-IL" : "en-US", {
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone: timezone,
		});
		return timeFormatter.format(currentDate);
	}
	// For 1-week and 2-week views, show date and time
	const dateTimeFormatter = new Intl.DateTimeFormat(i18n.language === "he" ? "he-IL" : "en-US", {
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZone: timezone,
	});
	return dateTimeFormatter.format(currentDate);
};

const getGraphMargins = () => {
	if (i18n.dir() === "rtl") {
		return { top: 5, right: 20, left: 30, bottom: 5 };
	}
	return { top: 5, right: 30, left: 20, bottom: 5 };
};

// Update the tooltip format function
const formatDateTooltip = (selectedTimeFrame: TimeFrame, date: string, timezone: string) => {
	if (!date) return "";

	const currentDate = new Date(date);
	const nextHour = new Date(currentDate);
	nextHour.setHours(currentDate.getHours() + 1);

	if (selectedTimeFrame === TimeFrame.REAL_TIME) {
		// For real-time, only show hours range
		const timeFormatter = new Intl.DateTimeFormat(i18n.language === "he" ? "he-IL" : "en-US", {
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone: timezone,
		});
		return `${timeFormatter.format(currentDate)} - ${timeFormatter.format(nextHour)}`;
	}
	// For other views, show full date and time range
	const dateTimeFormatter = new Intl.DateTimeFormat(i18n.language === "he" ? "he-IL" : "en-US", {
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZone: timezone,
	});
	return dateTimeFormatter.format(currentDate);
};

const formatTimeFrameLabel = (timeFrame: TimeFrame, i18n: any) => {
	const formatDate = (date: DateTime) => {
		return date.toFormat("d MMM");
	};

	if (timeFrame === TimeFrame.REAL_TIME) {
		return formatDate(DateTime.now());
	}

	const startDate = formatDate(DateTime.now());
	const endDate = formatDate(DateTime.now().plus({ days: timeFrame === TimeFrame.ONE_WEEK ? 7 : 14 }));
	return `${startDate} - ${endDate}`;
};

// Generic function to process time series data
const processTimeSeriesField = <T extends { date: DateTime }>(data: T[], sourceField: keyof T, futureField: keyof T, historicalField: keyof T) => {
	let finishFuture = false;
	return data
		?.slice()
		.reverse()
		.map((p) => {
			const currentTimestamp = DateTime.now();
			const pointTimestamp = p.date.toLocal();
			const processedPoint = { ...p };

			if (pointTimestamp > currentTimestamp) {
				processedPoint[futureField] = p[sourceField];
			} else {
				if (!finishFuture) {
					processedPoint[futureField] = p[sourceField];
					finishFuture = true;
				}
				processedPoint[historicalField] = p[sourceField];
			}

			return processedPoint;
		})
		.reverse();
};

export const CommandCenter = () => {
	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const [open, setOpen] = useState<boolean>(false);
	const { showSnackbar } = useSnackbar();
	const [selectedShift, setSelectedShift] = useState("Morning");
	const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.now());
	const tabWidth: number = 400;
	const ppn_diff: number = 0.7;
	const [departmentsSummaryData, setDepartmentsSummaryData] = useState<Array<DepartmentSummary>>([]);
	const [selectedTab, setSelectedTab] = useState(0);
	const [selectedDepartment, setSelectedDepartment] = useState<DepartmentSummary | null>(null);
	const [selectedTimeFrame, setSelectedTimeFrame] = useState<TimeFrame>(TimeFrame.REAL_TIME);
	const [isLoading, setIsLoading] = useState(false);
	const [bottomTab, setBottomTab] = useState(0);
	const [isPPNMode, setIsPPNMode] = useState(false);
	const ALL_DEPARTMENTS: string = "All Departments";
	const [department, setDepartment] = useState<string>(ALL_DEPARTMENTS);
	// Only hospital demos (Hospital type + inhouse2).
	const { timezone, chosenDepartment, getWeekIndexByDate, isHospitalView: isHospitalDemoView } = useHospitalData();
	// Demo: dark PPN line + turquoise target band; others keep CHART_COLORS.
	const cc = isHospitalDemoView ? { ...CHART_COLORS, PPN: DEMO_CHART.LINE, REFERENCE_BAND: DEMO_CHART.TARGET_BAND } : CHART_COLORS;
	const [shiftNo, setShiftNo] = useState<number>(0);
	const [commandCenterNurses, setCommandCenterNurses] = useState<CommandCenterNurse[]>([]);
	const { commandCenterNurses: rawNurses } = useCommandCenter({
		departmentId: chosenDepartment.department_id,
	});

	const weekIndex: number = useMemo(() => {
		return getWeekIndexByDate(selectedDate);
	}, [selectedDate, getWeekIndexByDate]);

	const stats: Record<string, number> = useMemo(() => {
		const newStats: Record<string, number> = { [ALL_DEPARTMENTS]: 0 };

		commandCenterNurses.forEach((nurse: CommandCenterNurse) => {
			const department: string = nurse.assignments[nurse.assignments.length - 1].id;
			newStats[department] = newStats[department] ? newStats[department]! + 1 : 1;
			newStats[ALL_DEPARTMENTS] += 1;
		});

		return newStats;
	}, [commandCenterNurses]);

	useEffect(() => {
		if (rawNurses) {
			setCommandCenterNurses(rawNurses);
		}
	}, [rawNurses]);

	const nurses: CommandCenterNurse[] = useMemo(() => {
		if (!commandCenterNurses.length) return [];

		if (department === ALL_DEPARTMENTS) return commandCenterNurses;

		const filteredNurses: CommandCenterNurse[] = commandCenterNurses.filter(
			(nurse: CommandCenterNurse) => nurse.assignments[nurse.assignments.length - 1].id === department,
		);

		return filteredNurses;
	}, [commandCenterNurses, department]);

	const handleRecommendationOpen = (): void => {
		setOpen(!open);
	};

	// Separate data fetching into a function for reusability
	const fetchDepartmentsData = async () => {
		setIsLoading(true);
		try {
			const response = await axiosWrapper.get("/command_center/departments_summary2", {
				params: {
					time_frame: selectedTimeFrame,
					hospital: chosenDepartment.hospital,
				},
			});

			let dataSummary = response.data as DepartmentSummary[];
			dataSummary.forEach((data) => {
				// Process census and admissions data separately
				const censusProcessed = processTimeSeriesField(data.census_graph || [], "census", "futureCensus", "historicalCensus");
				const admissionsProcessed = processTimeSeriesField(data.census_graph || [], "admissions", "futureAdmissions", "historicalAdmissions");

				// Combine and round the results
				data.census_graph = censusProcessed.map((point, index) => ({
					...point,
					...admissionsProcessed[index],
					futureCensus: point.futureCensus !== undefined ? Math.round(point.futureCensus as number) : undefined,
					futureAdmissions:
						admissionsProcessed[index].futureAdmissions !== undefined ? Math.round(admissionsProcessed[index].futureAdmissions as number) : undefined,
					historicalCensus: point.historicalCensus !== undefined ? Math.round(point.historicalCensus as number) : undefined,
					historicalAdmissions:
						admissionsProcessed[index].historicalAdmissions !== undefined ? Math.round(admissionsProcessed[index].historicalAdmissions as number) : undefined,
				}));

				// Process PPN data using the same generic function
				data.ppn_graph = processTimeSeriesField(data.ppn_graph || [], "ppn", "futurePPN", "historicalPPN");
			});
			// Calculate the summary of all departments
			const allDepartmentsSummary: DepartmentSummary = dataSummary.reduce(
				(acc, dept) => {
					acc.census += dept.census;
					acc.total_beds += dept.total_beds;
					acc.admissions += dept.admissions;
					acc.discharges += dept.discharges;
					acc.isolation_covid += dept.isolation_covid;
					acc.isolation_non_covid += dept.isolation_non_covid;
					acc.wls += dept.wls * dept.census;
					acc.money += dept.money;
					acc.actual_ppn += dept.actual_ppn;
					acc.target_ppn += dept.target_ppn;
					acc.previousDay += dept.previousDay;

					// Initialize ppn_graph from first department
					if (acc.ppn_graph.length === 0 && dept.ppn_graph) {
						acc.ppn_graph = deepCloneObject(dept.ppn_graph);
					} else if (dept.ppn_graph) {
						// Sum up ppn values for each time point
						acc.ppn_graph.forEach((point: PPNDataPoint, index) => {
							const deptPoint = dept.ppn_graph[index];
							if (deptPoint) {
								point.ppn += deptPoint.ppn;
								if (deptPoint.futurePPN !== undefined) {
									point.futurePPN = (point.futurePPN || 0) + deptPoint.futurePPN;
								}
								if (deptPoint.historicalPPN !== undefined) {
									point.historicalPPN = (point.historicalPPN || 0) + deptPoint.historicalPPN;
								}
								point.target_ppn_low += deptPoint.target_ppn_low;
								point.target_ppn_high += deptPoint.target_ppn_high;
							}
						});
					}

					if (acc.census_graph.length === 0) {
						acc.census_graph = deepCloneObject(dept.census_graph);
					} else {
						acc.census_graph.forEach((c: CensusDataPoint) => {
							const elem = dept.census_graph.find((p) => p.date === c.date);
							if (elem === undefined) {
								return;
							}

							c.admissions += elem.admissions;
							c.census += elem.census;
							c.census_avg_low = (c.census_avg_low || 0) + (elem.census_avg_low || 0);
							c.census_avg_high = (c.census_avg_high || 0) + (elem.census_avg_high || 0);
							if (c.futureAdmissions !== undefined && elem.futureAdmissions !== undefined) c.futureAdmissions += elem.futureAdmissions;
							if (c.futureCensus !== undefined && elem.futureCensus !== undefined) c.futureCensus += elem.futureCensus;
							if (c.historicalAdmissions !== undefined && elem.historicalAdmissions !== undefined) c.historicalAdmissions += elem.historicalAdmissions;
							if (c.historicalCensus !== undefined && elem.historicalCensus !== undefined) c.historicalCensus += elem.historicalCensus;
						});
					}
					return acc;
				},
				{
					name: "All Departments",
					display_name: t("All Departments"),
					date: dataSummary[0]?.date || DateTime.now(),
					shift_type: dataSummary[0]?.shift_type || "",
					nurses: departmentsSummaryData.reduce((sum, dept) => sum + dept.nurses, 0),
					census: 0,
					total_beds: 0,
					admissions: 0,
					discharges: 0,
					isolation_covid: 0,
					isolation_non_covid: 0,
					wls: 0,
					money: 0,
					actual_ppn: 0,
					target_ppn: 0,
					previousDay: 0,
					hourly_census: [],
					hourly_ppn: [],
					census_graph: [],
					ppn_graph: [],
				},
			);

			allDepartmentsSummary.wls = allDepartmentsSummary.wls / allDepartmentsSummary.census;

			// Calculate average PPN and target PPN for all departments
			allDepartmentsSummary.actual_ppn = Number((allDepartmentsSummary.actual_ppn / dataSummary.length).toFixed(2));
			allDepartmentsSummary.target_ppn = Number((allDepartmentsSummary.target_ppn / dataSummary.length).toFixed(2));

			// Calculate average values for ppn_graph
			if (allDepartmentsSummary.ppn_graph.length > 0) {
				const deptCount = dataSummary.length;
				allDepartmentsSummary.ppn_graph = allDepartmentsSummary.ppn_graph.map((point) => ({
					...point,
					ppn: point.ppn / deptCount,
					futurePPN: point.futurePPN !== undefined ? point.futurePPN / deptCount : undefined,
					historicalPPN: point.historicalPPN !== undefined ? point.historicalPPN / deptCount : undefined,
					target_ppn_low: point.target_ppn_low / deptCount,
					target_ppn_high: point.target_ppn_high / deptCount,
				}));
			}

			// Calculate the previous day data (as a single number, e.g., total census)
			allDepartmentsSummary.previousDay = dataSummary.reduce((acc, dept) => acc + dept.previousDay, 0);

			// Aggregate hourly census data (assuming all departments have the same hours)
			if (dataSummary.length > 0 && dataSummary[0].hourly_census) {
				allDepartmentsSummary.hourly_census = dataSummary[0].hourly_census.map((hourData, index) => ({
					hour: hourData.hour,
					census: dataSummary.reduce((sum, dept) => sum + (dept.hourly_census[index]?.census || 0), 0),
				}));
			}

			dataSummary = [allDepartmentsSummary, ...dataSummary];

			// Add this line to check if all departments have PPN data
			const is_ppn_mode = dataSummary.every((dept) => (dept.ppn_graph?.length ?? 0) > 0);

			dataSummary.forEach((d) => {
				d.ppn_graph.forEach((p) => (p.strDate = p.date.toISO() as string));
				d.census_graph.forEach((p) => (p.strDate = p.date.toISO() as string));
			});

			setDepartmentsSummaryData(dataSummary);
			// Store is_ppn_mode in state if you need it elsewhere
			setIsPPNMode(is_ppn_mode);
		} catch (err) {
			showSnackbar(t("Failed to get departments data"), "error");
		} finally {
			setIsLoading(false);
		}
	};

	// Effect for initial load and when dependencies change
	useEffect(() => {
		if (chosenDepartment.hospital) {
			fetchDepartmentsData();
		}
	}, [selectedTimeFrame, chosenDepartment.hospital]);

	const openPostShit = (event: React.MouseEvent<HTMLElement>): void => {
		event.preventDefault();
		setOpenDialog(true);
	};

	const handleShiftChanged = (event: SelectChangeEvent) => {
		setSelectedShift(event.target.value);
	};

	const [openDialog, setOpenDialog] = useState(false);
	const [selectedDept, setSelectedDept] = useState<DepartmentSummary>();

	const handleCardClick = (dept: any) => {
		setSelectedDepartment(dept);
		setSelectedTab(1);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	const handleDateChanged = (newValue: DateTime | null) => {
		setSelectedDate(newValue || selectedDate); // If newValue is null, keep the current date
	};

	const onSubmitRecommendation = useCallback(
		({ nurse, newDepartment, startDate, endDate }: FloatingNursesRecData): void => {
			const prevDepartmentIndex = departmentsSummaryData.findIndex(
				(summaryData: DepartmentSummary) => summaryData.name === nurse.assignments?.[nurse.assignments?.length - 1].id,
			);

			const newDepartmentIndex = departmentsSummaryData.findIndex((summaryData: DepartmentSummary) => summaryData.name === newDepartment);

			if (prevDepartmentIndex >= 0 || newDepartmentIndex >= 0) {
				if (nurse.assignments?.[nurse.assignments?.length - 1].id !== "Unassigned") {
					departmentsSummaryData[prevDepartmentIndex].actual_ppn = safeSum(departmentsSummaryData[prevDepartmentIndex].actual_ppn, ppn_diff);
				}
				departmentsSummaryData[newDepartmentIndex].actual_ppn = safeSub(departmentsSummaryData[newDepartmentIndex].actual_ppn, ppn_diff);

				const updatedNurseIndex = commandCenterNurses.findIndex((commandCenterNurse: CommandCenterNurse) => commandCenterNurse.id === nurse.id);

				if (updatedNurseIndex >= 0) {
					const updatedNurses: CommandCenterNurse[] = [...commandCenterNurses];
					const newAssignment: Assignment = {
						id: newDepartment,
						startDate,
						endDate,
					};
					updatedNurses[updatedNurseIndex] = {
						...updatedNurses[updatedNurseIndex],
						assignments: updatedNurses[updatedNurseIndex].assignments ? [...updatedNurses[updatedNurseIndex].assignments, newAssignment] : [newAssignment],
					};

					setCommandCenterNurses(updatedNurses);
					setDepartmentsSummaryData(departmentsSummaryData);
				}
			}
		},
		[departmentsSummaryData, commandCenterNurses],
	);

	const onDiscardRecommendation = useCallback(
		(nurse: CommandCenterNurse): void => {
			const updatedNurse: CommandCenterNurse = deepCloneObject(nurse);

			const currentAssignment: Assignment | undefined = updatedNurse.assignments?.pop();
			const previousAssignment: Assignment | undefined = updatedNurse.assignments?.[updatedNurse.assignments.length - 1];

			if (currentAssignment && previousAssignment) {
				const prevDepartmentIndex = departmentsSummaryData.findIndex((summaryData: DepartmentSummary) => summaryData.name === currentAssignment.id);

				const newDepartmentIndex = departmentsSummaryData.findIndex((summaryData: DepartmentSummary) => summaryData.name === previousAssignment.id);

				if (prevDepartmentIndex >= 0 || newDepartmentIndex >= 0) {
					if (previousAssignment.id !== "Unassigned") {
						departmentsSummaryData[newDepartmentIndex].actual_ppn = safeSub(departmentsSummaryData[newDepartmentIndex].actual_ppn, ppn_diff);
					}

					departmentsSummaryData[prevDepartmentIndex].actual_ppn = safeSum(departmentsSummaryData[prevDepartmentIndex].actual_ppn, ppn_diff);

					const updatedNurseIndex: number = commandCenterNurses.findIndex((commandCenterNurse: CommandCenterNurse) => commandCenterNurse.id === nurse.id);

					if (updatedNurseIndex >= 0) {
						const updatedNurses: CommandCenterNurse[] = [...commandCenterNurses];
						updatedNurses[updatedNurseIndex].assignments.pop();

						setCommandCenterNurses(updatedNurses);
						setDepartmentsSummaryData(departmentsSummaryData);
					}
				}
			}
		},
		[departmentsSummaryData, commandCenterNurses],
	);

	const padValue = (value: string | number): string => {
		const stringValue = String(value);
		return stringValue.length === 1 ? `${stringValue} \u00A0` : stringValue;
	};

	const StatItem = ({ value, label, additionalInfo }: any) => (
		<Grid className={Styles.statItem} container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center" }}>
			<span className={Styles.value}>{padValue(value)}</span>{" "}
			<span className={Styles.label} style={{ padding: "0px 5px" }}>
				{label}
				{additionalInfo && <span className="additional-info"> {additionalInfo}</span>}
			</span>
		</Grid>
	);

	const OverviewContent = () => (
		<div style={{ width: "100%", padding: "0px 15px" }}>
			<Grid
				container
				direction="row"
				sx={{
					justifyContent: "center",
					alignItems: "stretch",
					marginBottom: 1,
					width: "100%",
					paddingLeft: 2.5,
				}}
				spacing={6}
				wrap="nowrap"
			>
				<Grid size={6}>
					<Card
						elevation={0}
						style={{
							width: "100%",
							height: "100%",
							border: `1px solid ${NEUTRAL[400]}`,
							padding: "10px 4px",
						}}
					>
						<Grid
							container
							direction="row"
							sx={{
								justifyContent: i18n.dir() === "rtl" ? "flex-end" : "flex-start",
								alignItems: "center",
							}}
							wrap="nowrap"
						>
							{/* Bed + admissions icons are both 40px for everyone. */}
							<img
								src="/bed-occupancy.svg"
								alt={t("bed occupancy")}
								width={40}
								height={40}
								style={{
									margin: "0px 25px",
									order: i18n.dir() === "rtl" ? 2 : 1,
								}}
							/>
							<div
								style={{
									textAlign: i18n.dir() === "rtl" ? "right" : "left",
									order: i18n.dir() === "rtl" ? 1 : 2,
									width: "100%",
									padding: "0px 20px",
								}}
							>
								<h2
									style={{
										margin: 0,
										fontSize: 17,
										fontWeight: 500,
										textAlign: i18n.dir() === "rtl" ? "right" : "left",
									}}
								>
									{t("Bed Occupancy")}
								</h2>
								<h2
									style={{
										margin: 0,
										fontSize: 26,
										fontWeight: 500,
										textAlign: i18n.dir() === "rtl" ? "right" : "left",
									}}
								>
									{departmentsSummaryData.find((d) => d.name === "All Departments")?.census}
									<span style={{ fontWeight: 400, fontSize: 17 }}> {t("Occupied")} </span>
									<span style={{ fontWeight: 400 }}>
										/ {departmentsSummaryData.find((d) => d.name === "All Departments")?.total_beds}
										<span style={{ fontSize: 17, padding: "0px 10px" }}>{t("Total Beds")}</span>
									</span>
									<span style={{ fontWeight: 400, fontSize: 14 }}>
										{Math.round(
											(100 * (departmentsSummaryData.find((d) => d.name === "All Departments")?.census ?? 1)) /
												(departmentsSummaryData.find((d) => d.name === "All Departments")?.total_beds ?? 1),
										)}
										% {t("Occupancy")}
									</span>
								</h2>
							</div>
						</Grid>
					</Card>
				</Grid>

				<Grid size={6}>
					<Card
						elevation={0}
						style={{
							width: "100%",
							height: "100%",
							border: `1px solid ${NEUTRAL[400]}`,
							padding: "10px 4px",
						}}
					>
						<Grid container direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }} wrap="nowrap">
							{/* Size + spacing match the bed icon for everyone; demo also makes it black. */}
							<ThemedSvg
								src="/total-admissions.svg"
								color={isHospitalDemoView ? "#000000" : "var(--inhouse-primary)"}
								size={40}
								style={{ margin: "0px 25px" }}
							/>
							<div
								style={{
									textAlign: i18n.dir() === "rtl" ? "right" : "left",
									order: i18n.dir() === "rtl" ? 1 : 2,
									width: "100%",
									padding: "0px 20px",
								}}
							>
								<h2 style={{ margin: 0, fontSize: 17, fontWeight: 500 }}>{t("Total Admissions")}</h2>
								<h2 style={{ margin: 0, fontSize: 26, fontWeight: 500 }}>
									{departmentsSummaryData.find((d) => d.name === "All Departments")?.admissions?.toFixed(0)}
								</h2>
							</div>
						</Grid>
					</Card>
				</Grid>
			</Grid>
			<Grid
				container
				direction="row"
				sx={{
					justifyContent: "flex-start",
					alignItems: "stretch",
					width: "100%",
					mt: 3,
					flexGrow: 1,
					pr: "0.2vw",
					flexWrap: { xs: "wrap", md: "wrap" },
					"& > *": {
						width: {
							xs: "100%",
							sm: "calc(50% - 24px)",
							md: "calc(33.33% - 24px)",
							xl: isPPNMode ? "calc(25% - 24px)" : "calc(33.33% - 24px)",
						},
						minWidth: {
							xs: "unset",
							sm: "280px",
							md: "320px",
							xl: isPPNMode ? "280px" : "320px",
						},
						maxWidth: {
							md: "calc(33.33% - 24px)",
							xl: isPPNMode ? "calc(25% - 24px)" : "calc(33.33% - 24px)",
						},
						flexGrow: 0,
						flexShrink: 0,
					},
				}}
				spacing={3}
			>
				{departmentsSummaryData.map((dept) => (
					<Grid id={dept.name} key={dept.name}>
						<Card
							onClick={() => handleCardClick(dept)}
							sx={{
								cursor: "pointer",
								width: "100%",
								height: "100%",
								border: `1px solid ${NEUTRAL[400]}`,
								padding: "16px",
								transition: "box-shadow 0.1s ease-in-out",
								"&:hover": {
									boxShadow: `0px 5px 15px ${alpha(INK_BLACK, 0.2)}`,
								},
								direction: i18n.dir(),
							}}
							elevation={0}
						>
							<Grid
								container
								direction="row"
								// Demo adds space between the card title and the content below.
								sx={{ justifyContent: "space-between", alignItems: "center", mb: isHospitalDemoView ? 2 : 0 }}
							>
								<h2
									style={{
										fontSize: "20px",
										margin: i18n.dir() === "rtl" ? "0 0 0 5px" : "0 5px 0 0",
										fontWeight: 500,
									}}
								>
									{dept.display_name}
								</h2>
							</Grid>

							<Grid
								container
								direction="row"
								sx={{
									justifyContent: "space-between",
									// Demo top-aligns PPN with the right-side stats; others vertically center.
									alignItems: isHospitalDemoView ? "flex-start" : "center",
									flexDirection: { xs: "column", sm: "row" },
									gap: { xs: 2, sm: 0 },
									height: "100%",
								}}
								wrap="nowrap"
							>
								{/* Container for left side and divider */}
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										width: "50%",
										justifyContent: "center",
									}}
								>
									{isPPNMode ? (
										<PPNDisplay actualPPN={dept.actual_ppn} targetPPN={dept.target_ppn} smallScale={false} isDemo={isHospitalDemoView} />
									) : (
										<ShowBedOccupancy total_beds={dept.total_beds} census={dept.census} />
									)}
								</Box>

								<Divider
									orientation="vertical"
									flexItem
									sx={{
										margin: "0 15px",
										height: "80%",
									}}
								/>

								{/* Right side stats */}
								<Box
									sx={{
										width: "50%",
										display: "flex",
										flexDirection: "column",
										// Demo top-aligns the stats so they line up with the PPN text.
										justifyContent: isHospitalDemoView ? "flex-start" : "center",
									}}
								>
									<Grid
										className={Styles.departmentStats}
										container
										sx={{
											flexDirection: "column",
											justifyContent: "space-between",
											alignItems: i18n.dir() === "rtl" ? "flex-end" : "flex-start",
											padding: "0px 15px",
											height: "100%",
										}}
									>
										<StatItem
											value={
												i18n.dir() === "rtl"
													? `${padValue(dept.total_beds)} / ${padValue(dept.census)}`
													: `${padValue(dept.census)} / ${padValue(dept.total_beds)}`
											}
											label={`${t("Occupancy Rate")} ${padValue(((dept.census / dept.total_beds) * 100).toFixed(0))}%`}
										/>
										<StatItem value={padValue(get_dept_patient_score(dept))} label={t("Patient Score")} />
										<StatItem value={padValue(dept.admissions.toFixed(0))} label={t("Admissions")} />
									</Grid>
								</Box>
							</Grid>
						</Card>
					</Grid>
				))}
			</Grid>
			{selectedDept && (
				<Dialog data-testid="command-center-census-dialog" open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth dir={i18n.dir()}>
					<DialogTitle
						style={{
							fontSize: "32px",
							textAlign: i18n.dir() === "rtl" ? "right" : "left",
						}}
					>
						{t("Department Census", {
							department: selectedDept.name,
							date: selectedDate?.toFormat("yyyy-MM-dd"),
							shift: t(selectedShift),
						})}
					</DialogTitle>
					<DialogContent>
						<ResponsiveContainer width="100%" height={500}>
							<LineChart data={selectedDept?.hourly_census} margin={{ top: 30, right: 40, left: 30, bottom: 30 }}>
								<CartesianGrid stroke={NEUTRAL[150]} horizontal={true} vertical={true} strokeDasharray="3 3" />
								<XAxis
									dataKey="hour"
									label={{
										value: t("Hour"),
										position: "insideBottomRight",
										offset: -30,
										fontSize: 30,
									}}
									ticks={getEverySecondHour(selectedDept?.hourly_census)}
									tick={{ fontSize: 30 }}
									tickFormatter={(hour) => {
										const currentLang = i18n.language;
										return currentLang === "he" ? formatHourInHebrew(hour) : `${hour}:00`;
									}}
								/>
								<YAxis
									domain={[0, (dataMax: number) => Math.max(Math.ceil(selectedDept.total_beds * 1.2), Math.ceil(dataMax * 1.2))]}
									yAxisId="left"
									orientation={i18n.dir() === "rtl" ? "right" : "left"}
									label={{
										value: t("Census"),
										angle: -90,
										position: i18n.dir() === "rtl" ? "insideRight" : "insideLeft",
										offset: 10,
									}}
									tickCount={5}
									interval={0}
								/>
								<Tooltip
									formatter={(value, name) => [value, t("Census")]}
									contentStyle={{ fontSize: 30 }}
									labelFormatter={(hour) => {
										const currentLang = i18n.language;
										return currentLang === "he" ? formatHourInHebrew(Number(hour)) : `${hour}:00`;
									}}
								/>
								<Line type="monotone" dataKey="census" stroke={CHART_COLORS.CENSUS} strokeWidth={4} activeDot={{ r: 12 }} name={t("Census")} />
							</LineChart>
						</ResponsiveContainer>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);

	const AnalysisContent = () => {
		const [graphTab, setGraphTab] = useState(0);

		const handleGraphTabChange = (event: React.SyntheticEvent, newValue: number) => {
			setGraphTab(newValue);
		};

		const handleBottomTabChange = (event: React.SyntheticEvent, newValue: number) => {
			setBottomTab(newValue);
		};

		const CensusAdmissionGraph = () => {
			const [activePoint, setActivePoint] = useState<number | null>(null);

			if (!selectedDepartment) return <></>;

			// Find the last historical data point
			const lastHistoricalPoint = selectedDepartment.census_graph.find((point, index, array) => {
				return point.historicalCensus !== undefined && (index === array.length - 1 || array[index + 1].historicalCensus === undefined);
			});

			// Add this helper function to generate ticks every 2 numbers
			const generateYAxisTicks = (dataMin: number, dataMax: number) => {
				const ticks = [];
				const max = Math.max(Math.ceil(selectedDepartment?.total_beds * 1.2), Math.ceil(dataMax * 1.2));
				for (let i = 0; i <= max; i += 2) {
					ticks.push(i);
				}
				return ticks;
			};

			// Add this function before the return statement in CensusAdmissionGraph
			const transformedData = selectedDepartment.census_graph.map((point) => ({
				...point,
				census_range: [point.census_avg_low, point.census_avg_high] as [number, number],
			}));
			return (
				<Box sx={{ position: "relative", width: "100%", height: "100%" }}>
					{isLoading && (
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: alpha(SURFACE, 0.7),
								zIndex: 1,
							}}
						>
							<CircularProgress />
						</Box>
					)}
					<ResponsiveContainer width="100%" height="100%">
						<ComposedChart
							data={transformedData}
							margin={getGraphMargins()}
							onMouseMove={(state) => {
								if (state && state.activeTooltipIndex !== undefined) {
									setActivePoint(typeof state.activeTooltipIndex === "string" ? Number.parseInt(state.activeTooltipIndex) : state.activeTooltipIndex);
								}
							}}
							onMouseLeave={() => {
								setActivePoint(null);
							}}
						>
							<CartesianGrid stroke={alpha(INK.SECONDARY, 0.5)} horizontal={true} vertical={true} strokeDasharray="3 3" />
							<XAxis
								dataKey="strDate"
								tickFormatter={(value) => formatXAxisLabel(selectedTimeFrame, value, timezone)}
								textAnchor={i18n.dir() === "rtl" ? "end" : "middle"}
								reversed={i18n.dir() === "rtl"}
								tick={{
									fontSize: 12,
									fill: alpha(INK.SECONDARY, 0.5),
									textAnchor: i18n.dir() === "rtl" ? "start" : "end",
									dx: i18n.dir() === "rtl" ? 5 : -5,
								}}
								angle={-45}
								height={60}
								padding={{ left: 20, right: 20 }}
								axisLine={{ stroke: alpha(INK.SECONDARY, 0.5) }}
								tickLine={{ stroke: alpha(INK.SECONDARY, 0.5) }}
							/>
							<YAxis
								domain={[
									0,
									() => {
										// Find the maximum value between:
										// 1. Total beds
										// 2. Maximum census value
										// 3. Maximum admission value
										// 4. Maximum range value (census_avg_high)
										const maxValue = selectedDepartment.census_graph.reduce((max, point) => {
											const values = [selectedDepartment.total_beds, point.census || 0, point.admissions || 0, point.census_avg_high || 0];
											return Math.max(max, ...values);
										}, 0);

										// Add just a small buffer (10%) above the maximum value
										return Math.ceil(maxValue * 1.1);
									},
								]}
								yAxisId="left"
								orientation={i18n.dir() === "rtl" ? "right" : "left"}
								label={{
									value: t("Census"),
									angle: -90,
									position: i18n.dir() === "rtl" ? "insideRight" : "insideLeft",
									offset: 0,
									fill: alpha(INK.SECONDARY, 0.5),
								}}
								tick={{
									fill: alpha(INK.SECONDARY, 0.5),
									fontSize: 12,
									dx: i18n.dir() === "rtl" ? 20 : -20, // Move numbers away from axis
								}}
								axisLine={{ stroke: alpha(INK.SECONDARY, 0.5) }}
								tickLine={{ stroke: alpha(INK.SECONDARY, 0.5) }}
								ticks={generateYAxisTicks(
									0,
									selectedDepartment.census_graph.reduce(
										(max, point) => Math.max(max, point.census || 0, point.admissions || 0, point.census_avg_high || 0),
										0,
									),
								)}
							/>

							{/* Add the range area */}
							{
								<Area
									yAxisId="left"
									type="monotone"
									dataKey="census_range"
									stroke="none"
									// Demo: match the PPN tab (turquoise band); others keep the original purple census fill.
									fill={isHospitalDemoView ? DEMO_CHART.TARGET_BAND : CHART_COLORS.CENSUS}
									fillOpacity={isHospitalDemoView ? 1 : 0.1}
									name={t("Census Average")}
								/>
							}

							{/* Keep existing lines */}
							<Line
								yAxisId="left"
								type="monotone"
								connectNulls
								isAnimationActive={false}
								dataKey="futureCensus"
								name="Graph.Census"
								// Demo: blue census line (PPN-tab palette) instead of the purple.
								stroke={isHospitalDemoView ? DEMO_CHART.CENSUS_LINE : CHART_COLORS.CENSUS}
								dot={{
									r: 4,
									fill: SURFACE,
									stroke: isHospitalDemoView ? DEMO_CHART.CENSUS_LINE : CHART_COLORS.CENSUS,
									strokeWidth: 2,
									strokeDasharray: "0",
								}}
								strokeWidth={2}
								strokeDasharray="5 5"
								legendType="none"
								label={{
									position: "top",
									content: (props) => renderValueLabel(activePoint, props, CHART_COLORS.CENSUS_LABEL),
								}}
							/>
							<Line
								yAxisId="left"
								type="monotone"
								connectNulls
								isAnimationActive={false}
								dataKey="historicalCensus"
								name="Graph.Census"
								// Demo: blue census line (PPN-tab palette) instead of the purple.
								stroke={isHospitalDemoView ? DEMO_CHART.CENSUS_LINE : CHART_COLORS.CENSUS}
								dot={{
									stroke: isHospitalDemoView ? DEMO_CHART.CENSUS_LINE : CHART_COLORS.CENSUS,
									strokeWidth: 2,
									fill: isHospitalDemoView ? DEMO_CHART.CENSUS_LINE : CHART_COLORS.CENSUS,
									r: 4,
								}}
								strokeWidth={2}
								label={{
									position: "top",
									content: (props) => renderValueLabel(activePoint, props, CHART_COLORS.CENSUS_LABEL),
								}}
							/>

							{/* Admissions Lines */}
							<Line
								yAxisId="left"
								type="monotone"
								connectNulls
								isAnimationActive={false}
								dataKey="futureAdmissions"
								name="Admissions"
								stroke={cc.PPN}
								dot={{
									r: 4,
									fill: SURFACE,
									stroke: cc.PPN,
									strokeWidth: 2,
									strokeDasharray: "0",
								}}
								strokeWidth={2}
								strokeDasharray="5 5"
								legendType="none"
								label={{
									position: "top",
									content: (props) => renderValueLabel(activePoint, props, cc.PPN),
								}}
							/>
							<Line
								yAxisId="left"
								type="monotone"
								connectNulls
								isAnimationActive={false}
								dataKey="historicalAdmissions"
								name="Admissions"
								stroke={cc.PPN}
								dot={{
									stroke: cc.PPN,
									strokeWidth: 2,
									fill: cc.PPN,
									r: 4,
								}}
								strokeWidth={2}
								label={{
									position: "top",
									content: (props) => renderValueLabel(activePoint, props, cc.PPN),
								}}
							/>

							{/* Reference Lines */}
							{lastHistoricalPoint && (
								<ReferenceLine
									x={lastHistoricalPoint.strDate.toString()}
									stroke={cc.REFERENCE_BAND}
									yAxisId="left"
									label={{
										value: t("Now"),
										position: "top",
										fill: cc.REFERENCE_BAND,
									}}
								/>
							)}

							<ReferenceLine
								y={selectedDepartment.total_beds}
								stroke={CHART_COLORS.OUT_OF_BOUNDS}
								yAxisId="left"
								label={{
									value: t("# of beds"),
									position: "insideTop",
									offset: -15,
									fill: INK_BLACK,
									fontSize: 12,
								}}
								strokeWidth={2}
							/>

							<Tooltip
								cursor={false}
								content={({ active, payload, label }) => {
									if (active && payload && payload.length) {
										const censusValue = payload.find((p) => p.dataKey === "historicalCensus" || p.dataKey === "futureCensus")?.value;

										const rangePayload = payload.find((p) => p.dataKey === "census_range") as RangeDataPoint | undefined;

										const [rangeLow, rangeHigh] = rangePayload?.value || ["-", "-"];

										return (
											<div
												style={{
													backgroundColor: SURFACE,
													padding: "10px",
													border: `1px solid ${NEUTRAL[400]}`,
													borderRadius: "4px",
												}}
											>
												<p>{formatDateTooltip(selectedTimeFrame, label as string, timezone)}</p>
											</div>
										);
									}
									return null;
								}}
							/>
							<Legend
								formatter={(value) => t(value)}
								align={i18n.dir() === "rtl" ? "right" : "left"}
								wrapperStyle={{
									paddingTop: "10px",
								}}
							/>
						</ComposedChart>
					</ResponsiveContainer>
				</Box>
			);
		};

		useEffect(() => {
			if (departmentsSummaryData.length > 0 && !selectedDepartment) {
				setSelectedDepartment(departmentsSummaryData[0]);
			}
		}, [departmentsSummaryData]);

		const handleDepartmentClick = (dept: DepartmentSummary) => {
			setSelectedDepartment(dept);
		};

		// Add useEffect to update selected department data when timeframe changes
		useEffect(() => {
			if (selectedDepartment) {
				const dept = departmentsSummaryData.find((d) => d.name === selectedDepartment.name);
				if (dept) {
					setSelectedDepartment(dept);
				}
			}
		}, [selectedTimeFrame, departmentsSummaryData]);

		const PPNGraph = () => {
			const [activePoint, setActivePoint] = useState<number | null>(null);

			if (!selectedDepartment) return <></>;

			// Find the last historical data point
			const lastHistoricalPoint = selectedDepartment.census_graph.find((point, index, array) => {
				return point.historicalCensus !== undefined && (index === array.length - 1 || array[index + 1].historicalCensus === undefined);
			});

			// The target band travels on the chart's own data, so the Area needs no dataset of its own.
			const ppnGraphWithTargetRange = selectedDepartment.ppn_graph.map((point) => ({
				...point,
				target_ppn_range: [point.target_ppn_low, point.target_ppn_high] as [number, number],
			}));

			return (
				<Box sx={{ position: "relative", width: "100%", height: "100%" }}>
					{isLoading && (
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: alpha(SURFACE, 0.7),
								zIndex: 1,
							}}
						>
							<CircularProgress />
						</Box>
					)}
					<ResponsiveContainer width="100%" height="100%">
						<ComposedChart
							data={ppnGraphWithTargetRange}
							margin={getGraphMargins()}
							onMouseMove={(state) => {
								if (state && state.activeTooltipIndex !== undefined) {
									setActivePoint(state.activeTooltipIndex as number);
								}
							}}
							onMouseLeave={() => {
								setActivePoint(null);
							}}
						>
							<CartesianGrid stroke={alpha(INK.SECONDARY, 0.5)} horizontal={true} vertical={true} strokeDasharray="3 3" />
							<XAxis
								dataKey="strDate"
								tickFormatter={(value) => formatXAxisLabel(selectedTimeFrame, value, timezone)}
								textAnchor={i18n.dir() === "rtl" ? "end" : "middle"}
								reversed={i18n.dir() === "rtl"}
								tick={{
									fontSize: 12,
									fill: alpha(INK.SECONDARY, 0.5),
									textAnchor: i18n.dir() === "rtl" ? "start" : "end",
									dx: i18n.dir() === "rtl" ? 5 : -5,
								}}
								angle={-45}
								height={60}
								padding={{ left: 20, right: 20 }}
								axisLine={{ stroke: alpha(INK.SECONDARY, 0.5) }}
								tickLine={{ stroke: alpha(INK.SECONDARY, 0.5) }}
							/>
							<YAxis
								domain={[0, "auto"]}
								yAxisId="left"
								orientation={i18n.dir() === "rtl" ? "right" : "left"}
								label={{
									value: t("PPN"),
									angle: -90,
									position: i18n.dir() === "rtl" ? "insideRight" : "insideLeft",
									offset: 0,
									fill: alpha(INK.SECONDARY, 0.5),
								}}
								tick={{
									fill: alpha(INK.SECONDARY, 0.5),
									fontSize: 12,
									dx: i18n.dir() === "rtl" ? 20 : -20,
								}}
								axisLine={{ stroke: alpha(INK.SECONDARY, 0.5) }}
								tickLine={{ stroke: alpha(INK.SECONDARY, 0.5) }}
							/>

							{/* Future PPN Line */}
							<Line
								yAxisId="left"
								type="monotone"
								connectNulls
								isAnimationActive={false}
								dataKey="futurePPN"
								name={t("Actual PPN")}
								stroke={isHospitalDemoView ? DEMO_CHART.LINE : CHART_COLORS.CENSUS}
								dot={{
									r: 4,
									fill: SURFACE,
									stroke: isHospitalDemoView ? DEMO_CHART.LINE : CHART_COLORS.CENSUS,
									strokeWidth: 2,
									strokeDasharray: "0",
								}}
								strokeWidth={2}
								strokeDasharray="5 5"
								legendType="none"
								label={{
									position: "top",
									content: (props) => renderValueLabel(activePoint, props, CHART_COLORS.CENSUS_LABEL),
								}}
							/>

							{/* Historical PPN Line */}
							<Line
								yAxisId="left"
								type="monotone"
								connectNulls
								isAnimationActive={false}
								dataKey="historicalPPN"
								name={t("Actual PPN")}
								stroke={isHospitalDemoView ? DEMO_CHART.LINE : CHART_COLORS.CENSUS}
								dot={{
									stroke: isHospitalDemoView ? DEMO_CHART.LINE : CHART_COLORS.CENSUS,
									strokeWidth: 2,
									fill: isHospitalDemoView ? DEMO_CHART.LINE : CHART_COLORS.CENSUS,
									r: 4,
								}}
								strokeWidth={2}
								label={{
									position: "top",
									content: (props) => renderValueLabel(activePoint, props, CHART_COLORS.CENSUS_LABEL),
								}}
							/>

							{/* Add the target PPN range area */}
							{/* No `data` of its own: recharts 3 appends a child's dataset to the axis, so a second
							    24-hour copy here stretched the x-axis to two days. The band is on the chart data. */}
							<Area
								yAxisId="left"
								type="monotone"
								dataKey="target_ppn_range"
								stroke="none"
								// Demo: teal target band (more visible); non-demo keeps the census-tinted band.
								fill={isHospitalDemoView ? DEMO_CHART.TARGET_BAND : CHART_COLORS.CENSUS}
								fillOpacity={isHospitalDemoView ? 1 : 0.1}
								name={t("Target PPN Range")}
							/>

							<Tooltip
								cursor={false}
								content={({ active, payload, label }) => {
									if (active && payload && payload.length) {
										const ppnValue = payload.find((p) => p.dataKey === "historicalPPN" || p.dataKey === "futurePPN")?.value;

										const rangePayload = payload.find((p) => p.dataKey === "target_ppn_range") as RangeDataPoint | undefined;

										const [rangeLow, rangeHigh] = rangePayload?.value || ["-", "-"];

										return (
											<div
												style={{
													backgroundColor: SURFACE,
													padding: "10px",
													border: `1px solid ${NEUTRAL[400]}`,
													borderRadius: "4px",
												}}
											>
												<p>{formatDateTooltip(selectedTimeFrame, label as string, timezone)}</p>
											</div>
										);
									}
									return null;
								}}
							/>
							<Legend
								formatter={(value) => t(value)}
								align={i18n.dir() === "rtl" ? "right" : "left"}
								wrapperStyle={{
									paddingTop: "10px",
								}}
							/>
						</ComposedChart>
					</ResponsiveContainer>
				</Box>
			);
		};

		return (
			<Grid
				container
				spacing={1}
				sx={{
					height: "calc(100% - 128px)",
					width: "100%",
					padding: "0px 15px",
				}}
			>
				<Grid size={3} sx={{ height: "100%", minHeight: 0 }}>
					<Paper
						elevation={0}
						sx={{
							height: "100%",
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
							width: "calc(100% - 50px)",
						}}
					>
						<Typography variant="subtitle1" sx={{ padding: "10px 0px 10px 10px" }}>
							{departmentsSummaryData.length} {t("Departments")}
						</Typography>
						<List sx={{ flexGrow: 1, overflow: "auto", paddingBottom: "16px" }}>
							{/* Individual Departments */}
							{departmentsSummaryData.map((dept, index) => (
								<Card
									elevation={0}
									key={index}
									sx={{
										// borderBottom: index === departmentsSummaryData.length - 1 ? 'none' : '1px solid #e0e0e0',
										backgroundColor: selectedDepartment?.name === dept.name ? NEUTRAL[100] : "transparent",
										cursor: "pointer",
										p: 2,
										m: 1,
										mb: 2,
										border: `2px solid ${NEUTRAL[300]}`,
										borderColor: selectedDepartment?.name === dept.name ? INK_BLACK : NEUTRAL[300],
									}}
									onClick={() => handleDepartmentClick(dept)}
								>
									<Typography variant="subtitle1" component="div" sx={{ fontWeight: "bold", fontSize: "20px" }}>
										{dept.display_name}
									</Typography>
									{isPPNMode ? (
										<PPNDisplay actualPPN={dept.actual_ppn} targetPPN={dept.target_ppn} smallScale={true} isDemo={isHospitalDemoView} />
									) : (
										<ShowBedOccupancy total_beds={dept.total_beds} census={dept.census} />
									)}
								</Card>
							))}
						</List>
					</Paper>
				</Grid>

				{/* Right Side - Department Details */}
				{selectedDepartment && (
					<Grid size={9} container sx={{ flexDirection: "column", height: "100%", minHeight: 0, flexWrap: "nowrap" }}>
						{/* Top Row - Department Name and Key Metrics */}
						<Grid container spacing={1} sx={{ marginBottom: 1, pl: 2 }}>
							<Grid size={12} sx={{ gap: 3 }}>
								<Box sx={{ display: "flex", justifyContent: "space-between" }}>
									<Typography variant="h3" sx={{ marginBottom: 1, px: 4 }}>
										{selectedDepartment.display_name}
									</Typography>
									<IconButton data-testid="command-center-edit-shift-button" onClick={openPostShit}>
										<Edit />
									</IconButton>
								</Box>
							</Grid>
							{/* Key Metrics Cards */}

							<Grid size={6}>
								<Card
									elevation={0}
									sx={{
										height: "100%",
										border: `1px solid ${NEUTRAL[400]}`,
										padding: "4px",
										mr: 4,
									}}
								>
									<Grid container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center" }} wrap="nowrap">
										{/* Bed + admissions icons are both 40px for everyone. */}
										<img src="/bed-occupancy.svg" alt="bed occupancy" width={40} height={40} style={{ margin: "0px 25px" }} />
										<div>
											<Typography variant="subtitle2">{t("Bed Occupancy")}</Typography>
											<Typography variant="h6">
												{i18n.dir() === "rtl" ? (
													<>
														{" "}
														{selectedDepartment.total_beds} / {selectedDepartment.census}
													</>
												) : (
													<>
														{" "}
														{selectedDepartment.census} / {selectedDepartment.total_beds}
													</>
												)}
											</Typography>
										</div>
									</Grid>
								</Card>
							</Grid>

							<Grid size={6}>
								<Card
									elevation={0}
									sx={{
										height: "100%",
										border: `1px solid ${NEUTRAL[400]}`,
										padding: "4px",
									}}
								>
									<Grid container direction="row" sx={{ justifyContent: "flex-start", alignItems: "center" }} wrap="nowrap">
										{/* Size + spacing match the bed icon for everyone; demo also makes it black. */}
										<ThemedSvg
											src="/total-admissions.svg"
											alt="total admissions"
											color={isHospitalDemoView ? "#000000" : "var(--inhouse-primary)"}
											size={40}
											style={{ margin: "0px 25px" }}
										/>
										<div>
											<Typography variant="subtitle2">{t("Total Admissions")}</Typography>
											<Typography variant="h6">{selectedDepartment.admissions.toFixed(0)}</Typography>
										</div>
									</Grid>
								</Card>
							</Grid>
						</Grid>

						{/* Bottom section - Chart and AI Insights/Key Patients */}
						<Grid container sx={{ flexGrow: 1, height: "100%", minHeight: 0, pl: 2, pt: 2 }}>
							{/* Chart */}
							<Grid size={12} sx={{ height: "100%", paddingRight: 1, minHeight: 0 }}>
								<Paper
									elevation={1}
									sx={{
										height: "100%",
										padding: "16px",
										position: "relative",
										minHeight: 0,
										display: "flex",
										flexDirection: "column",
										mr: 3,
									}}
								>
									{isPPNMode ? (
										<>
											<Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
												<Tabs
													value={bottomTab}
													onChange={handleBottomTabChange}
													sx={(theme) => ({
														minHeight: "36px",
														"& .MuiTab-root": {
															minHeight: "36px",
															textTransform: "none",
															fontSize: "14px",
															fontWeight: "medium",
															color: theme.palette.primary.main,
															"&.Mui-selected": {
																color: INK_BLACK,
															},
														},
													})}
												>
													<Tab data-testid="command-center-ppn-tab" label={t("PPN")} />
													<Tab data-testid="command-center-census-tab" label={t("Census")} />
												</Tabs>
											</Box>
											<Typography sx={(theme) => ({ color: theme.palette.primary.main })} variant="h6" gutterBottom>
												{formatTimeFrameLabel(selectedTimeFrame, i18n)}
											</Typography>
											<Box sx={{ flexGrow: 1, minHeight: 0, paddingTop: "5px" }}>{bottomTab === 0 ? <PPNGraph /> : <CensusAdmissionGraph />}</Box>
										</>
									) : (
										<>
											<Typography sx={(theme) => ({ color: theme.palette.primary.main })} variant="h6" gutterBottom>
												{formatTimeFrameLabel(selectedTimeFrame, i18n)}
											</Typography>
											<Box sx={{ flexGrow: 1, minHeight: 0, paddingTop: "5px" }}>
												<CensusAdmissionGraph />
											</Box>
										</>
									)}
								</Paper>
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		);
	};

	// Update the tab change handler
	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setSelectedTab(newValue);
		if (newValue === 1 && !selectedDepartment) {
			// Reset selected department when switching to Analysis tab
			setSelectedDepartment(null);
		}
	};

	// Replace handleTimeFrameChange with this new function
	const handleTimeFrameChange = (newTimeFrame: TimeFrame) => {
		if (newTimeFrame !== selectedTimeFrame) {
			setSelectedTimeFrame(newTimeFrame);
			// Clear existing data before fetching new data
			setDepartmentsSummaryData([]);
		}
	};

	return (
		<div
			data-testid="command-center-root"
			data-hospital-demo={isHospitalDemoView ? "true" : undefined}
			style={{
				width: open ? `calc(100% - ${tabWidth}px)` : "100%",
				height: "calc(100% - 138px)",
				padding: "0px",
				paddingBottom: "10px",
				overflow: "auto",
				position: "fixed",
				top: "var(--app-header-height, 64px)",
				left: 0,
			}}
		>
			<Grid
				container
				direction="row"
				sx={{
					justifyContent: "flex-start",
					alignItems: "center",
					width: "100%",
					padding: "0px 15px",
					direction: i18n.dir(),
					boxShadow: `0px 4px 4px 0px ${alpha(theme.palette.primary.main, 0.2)}`,
					paddingBottom: "10px",
					paddingTop: "10px",
				}}
				wrap="nowrap"
			>
				<h1 style={{ fontSize: "18px", color: theme.palette.primary.main }}>{t("commandCenter.title")}</h1>
				<Divider orientation="vertical" flexItem sx={{ mx: 4 }} />

				<div>
					<Stack
						direction="row"
						spacing={1}
						sx={{
							height: "50px",
							alignItems: "center",
							"& .MuiChip-root": {
								borderRadius: "16px",
								fontWeight: "medium",
								cursor: "pointer",
								"&.MuiChip-outlined": {
									border: "none",
									background: "transparent",
									// Demo-only: blue to match the redesigned graph/buttons; else theme primary.
									color: isHospitalDemoView ? DEMO_BLUE : "primary.main",
									"&:hover": {
										background: alpha(isHospitalDemoView ? DEMO_BLUE : theme.palette.primary.main, 0.04),
									},
								},
								"&.MuiChip-filled": {
									background: isHospitalDemoView ? DEMO_BLUE : "primary.main",
									color: SURFACE,
									"&:hover": {
										background: isHospitalDemoView ? DEMO_BLUE_DARK : "primary.dark",
									},
								},
							},
						}}
					>
						<Chip
							data-testid="command-center-realtime-chip"
							label={t("commandCenter.tabs.realTime")}
							onClick={() => handleTimeFrameChange(TimeFrame.REAL_TIME)}
							color="primary"
							variant={selectedTimeFrame === TimeFrame.REAL_TIME ? "filled" : "outlined"}
							sx={{
								height: "32px",
								"& .MuiChip-label": {
									padding: selectedTimeFrame === TimeFrame.REAL_TIME ? "0 12px" : "0 8px",
								},
							}}
						/>
						<Chip
							data-testid="command-center-1week-chip"
							label={t("commandCenter.tabs.1Week")}
							onClick={() => handleTimeFrameChange(TimeFrame.ONE_WEEK)}
							color="primary"
							variant={selectedTimeFrame === TimeFrame.ONE_WEEK ? "filled" : "outlined"}
							sx={{
								height: "32px",
								"& .MuiChip-label": {
									padding: selectedTimeFrame === TimeFrame.ONE_WEEK ? "0 12px" : "0 8px",
								},
							}}
						/>
						<Chip
							data-testid="command-center-2week-chip"
							label={t("commandCenter.tabs.2Week")}
							onClick={() => handleTimeFrameChange(TimeFrame.TWO_WEEKS)}
							color="primary"
							variant={selectedTimeFrame === TimeFrame.TWO_WEEKS ? "filled" : "outlined"}
							sx={{
								height: "32px",
								"& .MuiChip-label": {
									padding: selectedTimeFrame === TimeFrame.TWO_WEEKS ? "0 12px" : "0 8px",
								},
							}}
						/>
					</Stack>
				</div>
				<TopRightPane>
					<CopilotButton
						open={open}
						onClick={() => {
							handleRecommendationOpen();
							trackEvent("floating_nurses_recommendation_open");
						}}
					/>
				</TopRightPane>
			</Grid>
			<Tabs
				data-testid="command-center-tabs"
				sx={{
					padding: "0px 15px 10px 15px",
				}}
				value={selectedTab}
				onChange={handleTabChange} // Updated to use new handler
				aria-label={t("command center tabs")}
			>
				<Tab data-testid="command-center-overview-tab" label={t("Overview")} />
				<Tab data-testid="command-center-analysis-tab" label={t("Analysis")} />
			</Tabs>
			{selectedTab === 0 ? <OverviewContent /> : <AnalysisContent />}
			<FloatingNursesRecommendation
				stats={stats}
				onSubmit={onSubmitRecommendation}
				onDiscard={onDiscardRecommendation}
				handleSelectDepartment={setDepartment}
				department={department}
				open={open}
				drawerWidth={tabWidth}
				nurses={nurses}
				handleDrawerClose={() => setOpen(false)}
			/>
			<Dialog
				data-testid="command-center-create-shift-dialog"
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				maxWidth="md"
				fullWidth
				keepMounted
				slotProps={{
					paper: {
						sx: {
							position: "absolute",
							top: "110px",
							height: "65vh",
							// Demo: wider so the Post Shift fields (Job/Slots/Time/Location) fit on one line.
							maxWidth: isHospitalDemoView ? "1150px !important" : "900px !important",
							width: isHospitalDemoView ? "1150px" : "900px",
							zIndex: 999999999999999,
						},
					},
				}}
			>
				{openDialog && (
					<CreateShiftContent
						role={inverseRolesMap[commandCenterNurses[0].role as NurseFullRole]}
						shiftNo={shiftNo}
						setShiftNo={setShiftNo}
						weekIndex={weekIndex}
						dayNo={selectedDate.weekday % 7}
						isMasterSchedule={false}
						onClose={() => setOpenDialog(false)}
					/>
				)}
			</Dialog>
		</div>
	);
};
